import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { Clock, Target } from 'lucide-react';
interface SlideTimeCalcProps {
  onContinue: () => void;
}
// Slide 3 — Internal Time Calculator
export function SlideProblem({
  onContinue
}: SlideTimeCalcProps) {
  const [hoursPerMoment, setHoursPerMoment] = useState<3 | 5 | 8>(5);
  const moments = 160;
  const totalHours = moments * hoursPerMoment;
  const ftePercent = Math.round(totalHours / 1800 * 100); // ~1800 productive hours per FTE year
  const hoursSpring = useSpring(0, {
    bounce: 0,
    duration: 600
  });
  const displayHours = useTransform(hoursSpring, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    hoursSpring.set(totalHours);
  }, [totalHours]);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-378">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-379">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-380">
          Internal Time
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-4" data-id="element-381">
          Your time should go to{' '}
          <span className="text-teal-400" data-id="element-382">brand decisions</span>, not campaign
          chasing.
        </h2>
        <p className="text-lg text-white/60 max-w-3xl" data-id="element-383">
          Every campaign-country moment creates coordination work: briefing,
          adapting, checking, reminding, reporting and optimizing.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mt-8" data-id="element-384">
        {/* LEFT — comparison split */}
        <div className="lg:col-span-7 grid grid-cols-2 gap-4" data-id="element-385">
          <motion.div initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} className="bg-navy-800/60 border border-navy-600 rounded-2xl p-6 flex flex-col h-full" data-id="element-386">
            <div className="flex items-center gap-2 mb-4" data-id="element-387">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center" data-id="element-388">
                <Clock className="w-4 h-4 text-red-400/80" data-id="element-389" />
              </div>
              <span className="text-white/40 uppercase text-[10px] tracking-widest font-medium" data-id="element-390">
                Execution drag
              </span>
            </div>
            <ul className="space-y-2.5 text-white/60 text-sm flex-1" data-id="element-391">
              <li className="flex items-start gap-2" data-id="element-392">
                <span className="text-red-400/60 mt-1" data-id="element-393">•</span>
                <span data-id="element-394">Briefing agencies, country teams, MLR</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-395">
                <span className="text-red-400/60 mt-1" data-id="element-396">•</span>
                <span data-id="element-397">Adapting content per market</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-398">
                <span className="text-red-400/60 mt-1" data-id="element-399">•</span>
                <span data-id="element-400">Chasing approvals & status updates</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-401">
                <span className="text-red-400/60 mt-1" data-id="element-402">•</span>
                <span data-id="element-403">Pulling reports, compiling spreadsheets</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-404">
                <span className="text-red-400/60 mt-1" data-id="element-405">•</span>
                <span data-id="element-406">Reminding, escalating, coordinating</span>
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} className="bg-teal-400/5 border border-teal-400/30 rounded-2xl p-6 flex flex-col h-full" data-id="element-407">
            <div className="flex items-center gap-2 mb-4" data-id="element-408">
              <div className="w-9 h-9 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center" data-id="element-409">
                <Target className="w-4 h-4 text-teal-400" data-id="element-410" />
              </div>
              <span className="text-teal-400 uppercase text-[10px] tracking-widest font-medium" data-id="element-411">
                Strategic time
              </span>
            </div>
            <ul className="space-y-2.5 text-white/80 text-sm flex-1" data-id="element-412">
              <li className="flex items-start gap-2" data-id="element-413">
                <span className="text-teal-400 mt-1" data-id="element-414">•</span>
                <span data-id="element-415">Brand & portfolio decisions</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-416">
                <span className="text-teal-400 mt-1" data-id="element-417">•</span>
                <span data-id="element-418">KOL relationships</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-419">
                <span className="text-teal-400 mt-1" data-id="element-420">•</span>
                <span data-id="element-421">Access & reimbursement work</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-422">
                <span className="text-teal-400 mt-1" data-id="element-423">•</span>
                <span data-id="element-424">Country prioritization</span>
              </li>
              <li className="flex items-start gap-2" data-id="element-425">
                <span className="text-teal-400 mt-1" data-id="element-426">•</span>
                <span data-id="element-427">Cross-functional alignment</span>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* RIGHT — calculator result */}
        <div className="lg:col-span-5 space-y-5" data-id="element-428">
          <div className="bg-navy-900/70 border border-navy-600 rounded-2xl p-6" data-id="element-429">
            <p className="text-white/50 text-xs uppercase tracking-wider mb-4 font-medium" data-id="element-430">
              Hours per campaign-country moment
            </p>
            <div className="grid grid-cols-3 gap-2" data-id="element-431">
              {[3, 5, 8].map(h => <button key={h} onClick={() => setHoursPerMoment(h as 3 | 5 | 8)} className={`py-3 rounded-xl font-medium text-sm transition-all ${hoursPerMoment === h ? 'bg-teal-400 text-navy-900' : 'bg-navy-700 text-white/60 hover:bg-navy-600'}`} data-id="element-432">
                  {h}h
                </button>)}
            </div>
            <p className="text-white/40 text-xs mt-3" data-id="element-433">
              160 moments × {hoursPerMoment} hours
            </p>
          </div>

          <div className="space-y-2" data-id="element-434">
            <p className="text-white/40 text-xs uppercase tracking-widest font-medium" data-id="element-435">
              Internal hours / year
            </p>
            <div className="flex items-baseline gap-2" data-id="element-436">
              <motion.span className="text-7xl md:text-8xl font-serif text-teal-400 tracking-tight leading-none" data-id="element-437">
                {displayHours}
              </motion.span>
              <span className="text-2xl text-white/40" data-id="element-438">hrs</span>
            </div>
            <p className="text-teal-300/80 text-sm mt-2" data-id="element-439">
              ≈ {ftePercent}% of a full-time role — pure coordination
            </p>
          </div>

          <p className="text-white/60 leading-relaxed text-sm border-l-2 border-teal-400/40 pl-3 py-1" data-id="element-440">
            That work matters. But it should not sit on the regional brand
            owner.
          </p>

          <Button onClick={onContinue} data-id="element-441">Shift the workload →</Button>
        </div>
      </div>
    </div>;
}